import { FC, useEffect, useState } from 'react';
import { useTowerLayout } from '../../TowerLayout/TowerLayoutProvider';
import InventoryOnPrem from './InventoryOnPrem';
import InventoryActiveDirectory from './InventoryActiveDirectory';
import CloudFrame, { isCloudMessageEventData } from '../CloudFrame/CloudFrame';
import { useAppDrawer } from '../AppDrawer/AppDrawerProvider';
import InvestigationDrawer from '../InvestigationDrawer/InvestigationDrawer';
import { useHistory } from 'react-router-dom';
import { usePricingModulesState } from '../../context/contexts/PricingModuleContext';
import { useIsCloudOnly } from '../SystemSettings/hooks/featureFlags/useIsCloudOnly';
import { httpGet, httpPost } from '../../components/utils';

type InventoryConfig = {
    hideCloudTeaser?: boolean;
};

const Inventory: FC = () => {
    const { isOfflineMode, setHideHeader } = useTowerLayout();
    const { closeAppDrawer, displayContent } = useAppDrawer();

    const [displayAdInventory, setDisplayAdInventory] = useState<boolean>(false);
    const [cloudFrameMargin, setCloudFrameMargin] = useState({ height: 64, width: 213 });
    const [inventoryConfig, setInventoryConfig] = useState<InventoryConfig | null>(null);
    const history = useHistory();
    const { pricingModulesState } = usePricingModulesState();
    const isCloudOnly = useIsCloudOnly();

    useEffect(() => {
        const getConfig = async () => {
            try {
                const res = await httpGet('/inventory/config');
                if (res && res.data) {
                    setInventoryConfig(res.data);
                }
            } catch (error) {
                console.error('Failed to fetch inventory configuration:', error);
            }
        };
        getConfig();
    }, []);

    const updateConfig = async (config: InventoryConfig) => {
        try {
            await httpPost('/inventory/config', config);
            setInventoryConfig(config);
        } catch (error) {
            console.error('Failed to update inventory configuration:', error);
        }
    };

    const onPremMode = isOfflineMode || (pricingModulesState && !pricingModulesState.basePlatform?.cloudEnabled);

    useEffect(() => {
        !onPremMode && setHideHeader(true);

        const params = new URLSearchParams(window.location.search);
        const guid = params.get('investigateGuid');
        const adType = params.get('investigateAdType');
        const identifier = params.get('investigateIdentifier');
        if ((guid || identifier) && adType && parseInt(adType)) {
            displayContent(
                <InvestigationDrawer guid={guid || ''} identifier={identifier || ''} adType={parseInt(adType)} />
            );
        }

        return () => {
            setHideHeader(false);
            closeAppDrawer();
        };
    }, [isOfflineMode, setHideHeader]);

    if (onPremMode) {
        return (
            <InventoryOnPrem
                shouldShowGetCloud={!isOfflineMode}
                inventoryConfig={inventoryConfig}
                updateInventoryConfig={updateConfig}
            />
        );
    }

    if (isOfflineMode === false) {
        return (
            <div className="relative h-[calc(100vh-64px)] w-[calc(100vw-277px)]">
                {displayAdInventory && !isCloudOnly && (
                    <div
                        style={{
                            top: cloudFrameMargin.height,
                            left: cloudFrameMargin.width + 77.5,
                            height: `calc(100vh - ${cloudFrameMargin.height}px)`,
                            width: `calc(100vw - ${cloudFrameMargin.width + 77.5}px)`
                        }}
                        className={`absolute z-[1000]`}>
                        <InventoryActiveDirectory />
                    </div>
                )}

                <div className="h-screen w-[calc(100vw-64px)] ml-16 bg-sf-white absolute z-[100]">
                    <CloudFrame
                        srcRoute={`/silverfort/v1/inventory`}
                        fallback={
                            isCloudOnly ? undefined : (
                                <InventoryOnPrem
                                    inFrame={true}
                                    inventoryConfig={inventoryConfig}
                                    updateInventoryConfig={updateConfig}
                                />
                            )
                        }
                        customEventHandler={(event) => {
                            if (event.data === 'NAVIGATE_TO_CLOUD_INTEGRATIONS_SETTINGS') {
                                history.push('/Settings/Cloud%20Integrations');
                            } else if (
                                isCloudMessageEventData(event.data) &&
                                event.data.source === 'CLOUD-INVENTORY-COMPONENT'
                            ) {
                                try {
                                    const cloudFrameData = event.data?.data;
                                    setDisplayAdInventory(cloudFrameData.active ?? false);
                                    setCloudFrameMargin({
                                        height: cloudFrameData.headerHeight,
                                        width: cloudFrameData.menuWidth
                                    });
                                } catch (e) {
                                    console.error('failed to parse cloud frame data as JSON');
                                }
                            }
                        }}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="silverfort-loader">
            <div className="logo"></div>
        </div>
    );
};

export default Inventory;
